import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input, InputGroup, InputLeftElement, Tooltip } from "@chakra-ui/react";
import { FieldWrapper } from "../../components/form/FieldWrapper";
import { NullableSwitch } from "../../components/form/NullableSwitch";
import { useDisabled, useNAState, isEmptyValue } from "../../utils/formField";
import { validateField as validateFieldAPI } from "../../ServiceRequest";
import { runValidator } from "../../utils/validatorFunctions";

type Props = {
  field: any;
  value?: string | number;
  readOnly?: boolean;
  invalid?: boolean;
  submitted?: boolean;
  templateName: string;
  documentId?: string;
};

const MAX_INT = 10_000_000;

function sanitizeToRawDecimal(input: string): string {
  const original = input ?? "";
  let s = original.replace(/,/g, "").replace(/[^0-9.,]/g, "");
  const endsWithSep = /[.,]$/.test(s);
  const firstSep = s.search(/[.,]/);
  if (firstSep !== -1) {
    const head = s.slice(0, firstSep);
    const tail = s.slice(firstSep + 1).replace(/[.,]/g, "");
    s = `${head}.${tail}`;
  }
  let [intPart, decPart = ""] = s.split(".");
  intPart = intPart.replace(/^0+(?=\d)/, "") || (decPart || endsWithSep ? "0" : "");
  if (decPart.length > 2) decPart = decPart.slice(0, 2);
  if (endsWithSep && decPart.length === 0) return `${intPart}.`;
  return decPart ? `${intPart}.${decPart}` : intPart;
}

function formatUSDDisplayFromRaw(raw: string): string {
  if (!raw) return "";
  const endsWithDot = raw.endsWith(".");
  const [i, d = ""] = raw.split(".");
  const intFormatted = i.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (endsWithDot) return `${intFormatted}.`;
  return d ? `${intFormatted}.${d}` : intFormatted;
}

function exceedsMax(raw: string): boolean {
  if (!raw) return false;
  const n = parseFloat(raw);
  return !Number.isNaN(n) && Math.floor(Math.abs(n)) > MAX_INT;
}

const isTransientDecimal = (s: string) => s === "" || s === "." || /^\d+\.$/.test(s);

const NumberField: React.FC<Props> = ({
  field,
  value = "",
  readOnly,
  invalid,
  submitted = false,
  templateName,
  documentId,
}) => {
  const nullable = field?.nullable === true;
  const isUSD = field?.format === "usd";

  const { mode, setMode, text, setText } = useNAState({ value, nullable });
  const { ro, disabled } = useDisabled({ readOnly, nullable, mode });

  const requiredNow = !nullable || mode === "val";
  const derivedInvalid = !!invalid || (submitted && requiredNow && isEmptyValue(text));

  const inputRef = useRef<HTMLInputElement>(null);
  const [serverErr, setServerErr] = useState<string | null>(null);

  const [limitOpen, setLimitOpen] = useState(false);
  const limitTimer = useRef<number | null>(null);
  const showLimitTip = () => {
    if (limitTimer.current) window.clearTimeout(limitTimer.current);
    setLimitOpen(true);
    limitTimer.current = window.setTimeout(() => setLimitOpen(false), 1500);
  };

  const displayValue = useMemo(() => {
    if (!isUSD) return String(text ?? "");
    return formatUSDDisplayFromRaw(String(text ?? ""));
  }, [isUSD, text]);

  const lastAcceptedDisplayRef = useRef<string>("");
  const lastAcceptedRawRef = useRef<string>("");

  useEffect(() => {
    lastAcceptedDisplayRef.current = displayValue;
    lastAcceptedRawRef.current = String(text ?? "");
  }, [displayValue, text]);

  const moveCaretToEnd = () => {
    queueMicrotask(() => {
      const el = inputRef.current;
      if (!el) return;
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch {}
    });
  };

  const revertDOMToLastAccepted = () => {
    const el = inputRef.current;
    if (!el) return;
    el.value = lastAcceptedDisplayRef.current;
    moveCaretToEnd();
  };

  const emitAccepted = (raw: string) => {
    const el = inputRef.current;
    if (!el) return;
    const ev = new CustomEvent("calc:accepted", {
      bubbles: true,
      detail: { raw, display: formatUSDDisplayFromRaw(raw) },
    });
    el.dispatchEvent(ev);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const buildProposedDisplay = (data: string | null, inputType: string) => {
    const el = inputRef.current;
    const base = displayValue;
    if (!el) return base;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;

    if (!data || data.length === 0) {
      return base.slice(0, start) + base.slice(end);
    }
    return base.slice(0, start) + data + base.slice(end);
  };

  const handleBeforeInput = (
    e: React.FormEvent<HTMLInputElement> & { data?: string; inputType?: string }
  ) => {
    if (!isUSD || disabled || ro) return;
    const data = (e as any).data as string | null;
    const inputType = (e as any).inputType as string | undefined;
    const willEditContent = inputType?.startsWith("insert") || inputType?.startsWith("delete");
    if (!willEditContent) return;

    const proposedDisplay = buildProposedDisplay(data, inputType || "insertText");
    const candidateRaw = sanitizeToRawDecimal(proposedDisplay);

    if (exceedsMax(candidateRaw)) {
      e.preventDefault();
      e.stopPropagation();
      showLimitTip();
      revertDOMToLastAccepted();
    }
  };

  const trySetRaw = (candidate: string) => {
    if (isTransientDecimal(candidate)) {
      setServerErr(null);
      setText(candidate);
      if (inputRef.current) inputRef.current.value = formatUSDDisplayFromRaw(candidate);
      moveCaretToEnd();
      return;
    }

    if (field?.validatorFunction) {
      const r = runValidator(field.validatorFunction, field.validatorArguments, candidate);
      if (!r.ok) {
        setServerErr(r.message || "Invalid value");
        revertDOMToLastAccepted();
        return;
      }
    }
    if (exceedsMax(candidate)) {
      showLimitTip();
      revertDOMToLastAccepted();
      return;
    }
    setServerErr(null);
    setText(candidate);
    const el = inputRef.current;
    if (el) {
      el.value = formatUSDDisplayFromRaw(candidate);
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.dispatchEvent(new CustomEvent("calc:accepted", {
        bubbles: true,
        detail: { raw: candidate, display: formatUSDDisplayFromRaw(candidate) },
      }));
    }
    moveCaretToEnd();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!isUSD) return;
    e.preventDefault();
    const raw = sanitizeToRawDecimal(e.clipboardData.getData("text"));
    queueMicrotask(() => trySetRaw(raw));
  };

  const handleDrop = (e: React.DragEvent<HTMLInputElement>) => {
    if (!isUSD) return;
    e.preventDefault();
    const text = e.dataTransfer.getData("text") || "";
    const raw = sanitizeToRawDecimal(text);
    queueMicrotask(() => trySetRaw(raw));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isUSD) {
      setText(e.target.value);
      return;
    }
    const raw = sanitizeToRawDecimal(e.target.value);
    trySetRaw(raw);
  };

  const serverAbortRef = useRef<AbortController | null>(null);
  const handleBlur = async () => {
    if (ro || disabled) return;

    const rawToSend = isUSD
      ? sanitizeToRawDecimal(inputRef.current?.value ?? "")
      : String(text ?? "");

    if (rawToSend === "." || rawToSend === "") {
      setText("");
      setServerErr(null);
      if (isUSD && inputRef.current) inputRef.current.value = "";
      return;
    }
    if (rawToSend === "N/A") {
      setServerErr(null);
      return;
    }

    serverAbortRef.current?.abort();
    const ac = new AbortController();
    serverAbortRef.current = ac;

    try {
      const res: any = await validateFieldAPI(
        templateName,
        field.name,
        String(rawToSend),
        documentId
      );
      if (ac.signal.aborted) return;

      if (res?.valid) {
        setServerErr(null);
        if (res?.normalizedValue != null && res.normalizedValue !== rawToSend) {
          setText(res.normalizedValue);
          emitAccepted(res.normalizedValue);
          if (isUSD && inputRef.current) {
            inputRef.current.value = formatUSDDisplayFromRaw(res.normalizedValue);
          }
        }
      } else {
        const msg = res?.errors?.[0]?.message || "Invalid value";
        setServerErr(msg);
      }
    } catch {
    }
  };

  const handleFocus = () => { if (isUSD) moveCaretToEnd(); };
  const handleCompositionEnd = () => { if (isUSD) moveCaretToEnd(); };

  const hasError = derivedInvalid || !!serverErr;

  return (
    <FieldWrapper field={field} invalid={hasError}>
      <NullableSwitch nullable={nullable} mode={mode} setMode={setMode} readOnly={ro}>
        {isUSD ? (
          <Tooltip
            isOpen={limitOpen}
            placement="bottom-start"
            label="Max value is 10,000,000"
            hasArrow={false}
            bg="black"
            color="white"
            gutter={8}
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">$</InputLeftElement>
              <Input
                ref={inputRef}
                data-calc-source="true"
                name={!ro && requiredNow ? field.name : undefined}
                type="text"
                value={displayValue}
                required={false}
                onInvalid={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if (
                    (e.key >= "0" && e.key <= "9") ||
                    ["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Home","End"].includes(e.key)
                  ) return;
                  if (e.key === "." || e.key === ",") return;
                  e.preventDefault();
                }}
                onBeforeInput={handleBeforeInput as any}
                onChange={handleChange}
                onPaste={handlePaste}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onDrop={handleDrop}
                onCompositionEnd={handleCompositionEnd}
                isDisabled={disabled}
                readOnly={ro}
                inputMode="decimal"
                placeholder="0.00"
                aria-required={false}
              />
            </InputGroup>
          </Tooltip>
        ) : (
          <Input
            name={!ro && requiredNow ? field.name : undefined}
            type="number"
            step="any"
            lang="en-US"
            required={false}
            onInvalid={(e) => e.preventDefault()}
            value={text as any}
            max={MAX_INT}
            onChange={(e) => setText(e.target.value)}
            onBlur={handleBlur}
            isDisabled={disabled}
            readOnly={ro}
            inputMode="decimal"
            placeholder="0.00"
            aria-required={false}
          />
        )}
      </NullableSwitch>

      {ro && (
        <input
          type="hidden"
          name={field.name}
          value={nullable ? (mode === "na" ? "N/A" : String(text ?? "")) : String(text ?? "")}
        />
      )}
      {!ro && nullable && mode === "na" && <input type="hidden" name={field.name} value="N/A" />}
    </FieldWrapper>
  );
};

export default NumberField;
